import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ShieldAlert,
  Heart,
  BookOpen,
  LifeBuoy,
  Ban,
  UserCheck,
  Wind,
  ClipboardList,
  Brain,
  NotebookPen,
  Users,
  CalendarDays,
  Globe,
  Sparkles,
  Scale,
  Mail,
  AlertTriangle,
} from "lucide-react";
import { CompanyInfo } from "@/components/CompanyInfo";

const LAST_UPDATED = "13 September 2026";

export const Route = createFileRoute("/disclaimer")({
  head: () => ({
    meta: [
      { title: "Full Disclaimer — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Recovery Trauma™ is a trauma-informed wellbeing, educational, self-help, community and therapist-discovery platform for adults — not therapy, medical treatment, diagnosis or a crisis service.",
      },
      { property: "og:title", content: "Full Disclaimer — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "What Recovery Trauma™ is, what it is not, and where to get urgent help.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/disclaimer" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/disclaimer" },
    ],
  }),
  component: DisclaimerPage,
});

function Section({
  icon: Icon,
  title,
  tone = "default",
  children,
}: {
  icon: React.ElementType;
  title: string;
  tone?: "default" | "warning" | "gold";
  children: React.ReactNode;
}) {
  const shell =
    tone === "warning"
      ? "rounded-2xl border border-amber-300/50 bg-amber-50/60 p-6"
      : tone === "gold"
        ? "rounded-2xl border border-gold/30 bg-gold/5 p-6"
        : "rounded-2xl border border-border bg-card p-6 shadow-sm";
  const badge =
    tone === "warning" ? "bg-amber-100 text-amber-700" : "bg-gold/15 text-gold";
  return (
    <section className={shell}>
      <div className="mb-3 flex items-center gap-3">
        <div
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${badge}`}
        >
          <Icon className="h-[18px] w-[18px]" />
        </div>
        <h2 className="font-serif text-xl text-foreground">{title}</h2>
      </div>
      <div className="space-y-3 text-sm leading-relaxed text-foreground/85">
        {children}
      </div>
    </section>
  );
}

function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="list-inside list-disc space-y-1">
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

function Sub({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="pt-1 font-serif text-base text-foreground">{children}</h3>
  );
}

function DisclaimerPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <header className="mb-8 mt-4 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <ShieldAlert className="h-3.5 w-3.5" /> Important Notice
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
          Full Disclaimer
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
          Please read this carefully. Recovery Trauma™ is here to offer
          trauma-informed wellbeing, educational and self-help resources — it
          does not replace professional healthcare or emergency support.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          Last updated: {LAST_UPDATED}
        </p>
      </header>

      {/* Urgent help first */}
      <div className="mb-6 rounded-2xl border border-rose-300/50 bg-rose-50/60 p-6">
        <div className="mb-2 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-100 text-rose-700">
            <LifeBuoy className="h-[18px] w-[18px]" />
          </div>
          <h2 className="font-serif text-xl text-foreground">
            If you need urgent help
          </h2>
        </div>
        <div className="space-y-3 text-sm leading-relaxed text-foreground/85">
          <p>
            Recovery Trauma™ is not an emergency or crisis service and is not
            continuously monitored for emergencies.
          </p>
          <p>
            <strong>
              If you or someone else is in immediate danger, or there is a
              life-threatening emergency, contact your local emergency service
              straight away.
            </strong>
          </p>
          <Sub>United Kingdom</Sub>
          <p>
            Immediate danger or a life-threatening emergency:{" "}
            <strong>call 999 or go to A&amp;E</strong>.
          </p>
          <p>
            Emotional support in the UK and Republic of Ireland:{" "}
            <strong>Samaritans — 116 123</strong>, free, day and night.
          </p>
          <Sub>United States</Sub>
          <p>
            Suicide or mental-health crisis support:{" "}
            <strong>call or text 988</strong> (Suicide &amp; Crisis Lifeline).
            For immediate danger, call 911.
          </p>
          <Sub>Other countries</Sub>
          <p>
            Please use the emergency or crisis service for your location. Our
            support page lists services by country.
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Link
              to="/support"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              View Support Resources
            </Link>
            <Link
              to="/crisis"
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              Crisis Support
            </Link>
          </div>
        </div>
      </div>

      <div className="space-y-5">
        {/* 1 */}
        <Section icon={Heart} title="1. What Recovery Trauma™ is">
          <p>
            Recovery Trauma™ is a{" "}
            <strong>
              trauma-informed wellbeing, educational, self-help, community and
              therapist-discovery platform
            </strong>{" "}
            for adults.
          </p>
          <p>The platform offers resources that may support:</p>
          <Bullets
            items={[
              "personal reflection",
              "wellbeing",
              "grounding",
              "nervous-system regulation",
              "mindfulness",
              "self-awareness",
              "journaling",
              "emotional understanding",
              "trauma-informed education",
              "peer and community support",
              "discovery of independent therapists",
            ]}
          />
          <p>
            This includes educational content, self-help tools, grounding and
            regulation exercises, breathing, meditation, somatic and wellbeing
            practices, journals and reflective tools, assessments and
            questionnaires, the Emotion Wheel, Inner Child and Shadow Work
            exercises, community, events and seminars, therapist discovery,
            AI-enabled features where applicable, and audio, sound and sleep
            content.
          </p>
          <p>
            Everything here is offered as general educational and wellbeing
            support. Individual experiences and responses to wellbeing
            activities vary.
          </p>
        </Section>

        {/* 2 */}
        <Section icon={Ban} tone="warning" title="2. What Recovery Trauma™ is not">
          <p>Recovery Trauma™ is not:</p>
          <Bullets
            items={[
              "therapy",
              "counselling",
              "psychotherapy",
              "medical treatment",
              "mental-health treatment",
              "clinical care",
              "psychological assessment",
              "medical diagnosis",
              "mental-health diagnosis",
              "a healthcare provider",
              "a crisis service",
              "an emergency service",
            ]}
          />
          <p>
            Recovery Trauma™ does not diagnose, treat, cure or prevent any
            medical or mental-health condition.
          </p>
          <p>
            Nothing provided through the platform creates a therapist–client,
            counsellor–client, doctor–patient or other healthcare-professional
            relationship between you and Recovery Trauma™ or Jana Burkhardt.
          </p>
        </Section>

        {/* 3 */}
        <Section icon={UserCheck} title="3. Founder boundary">
          <p>
            Jana Burkhardt is the <strong>Founder &amp; Product Compliance
            Lead</strong> of Recovery Trauma™ and currently also acts as{" "}
            <strong>Platform Administrator</strong>. Both roles are{" "}
            <strong>non-clinical</strong>.
          </p>
          <p>Jana does not provide, through Recovery Trauma™:</p>
          <Bullets
            items={[
              "therapy",
              "counselling",
              "psychotherapy",
              "diagnosis",
              "clinical assessment",
              "medical advice",
              "treatment",
              "crisis intervention",
              "clinical supervision",
            ]}
          />
          <p>
            Her responsibilities relate to the platform itself: governance,
            compliance, administration, the safeguarding framework and
            operational oversight. You can read more on our{" "}
            <Link to="/governance" className="text-gold underline">
              governance page
            </Link>
            .
          </p>
        </Section>

        {/* 4 */}
        <Section icon={BookOpen} title="4. Educational and wellbeing content">
          <p>
            Information on Recovery Trauma™ is provided for general educational,
            informational, wellbeing and self-help purposes. It is not
            personalised professional advice.
          </p>
          <p>
            Please do not use platform content as a substitute for advice,
            assessment or treatment from an appropriately qualified healthcare
            or mental-health professional where that support is needed.
          </p>
        </Section>

        {/* 5 */}
        <Section icon={Wind} title="5. Wellbeing practices">
          <p>
            This includes breathwork, grounding, meditation, yoga, somatic
            exercises, EFT, relaxation, bilateral exercises and sleep exercises.
          </p>
          <p>
            <strong>Not every practice suits every person.</strong> Trauma
            responses are individual, and an exercise that feels settling for
            one person can feel activating for another.
          </p>
          <Bullets
            items={[
              "Choose activities according to your own circumstances.",
              "Stop an activity if it causes significant discomfort or feels unsuitable for you.",
              "Seek appropriate professional advice if you are unsure whether an activity is suitable for you, particularly if you have a physical or mental-health condition, are pregnant, or are recovering from injury or surgery.",
            ]}
          />
          <p>
            These practices are offered as wellbeing support. They are not
            presented as a treatment for trauma or for any particular condition.
          </p>
        </Section>

        {/* 6 */}
        <Section icon={ClipboardList} title="6. Assessments, questionnaires and scores">
          <p>
            Any questionnaire, quiz, assessment, self-check, score, result or
            reflection prompt on Recovery Trauma™ is for{" "}
            <strong>education and self-reflection only</strong>.
          </p>
          <p>These tools are not:</p>
          <Bullets
            items={[
              "diagnostic tests",
              "clinical assessments",
              "psychological evaluations",
              "medical examinations",
            ]}
          />
          <p>
            A score or result must not be used to diagnose or rule out any
            mental-health or medical condition. If you have concerns about
            symptoms or your mental health, please seek an appropriate
            professional assessment.
          </p>
        </Section>

        {/* 7 */}
        <Section icon={Brain} title="7. AI-generated and AI-assisted content">
          <p>
            Some optional features use AI — for example journal and dream
            reflections, the in-app guide and support chat, voice transcription
            and guided audio narration.
          </p>
          <p>
            <strong>
              AI output may be incomplete, inaccurate or unsuitable for your
              particular circumstances.
            </strong>{" "}
            It is provided for educational and informational purposes and must
            not be relied on for:
          </p>
          <Bullets
            items={[
              "diagnosis",
              "treatment",
              "clinical decision-making",
              "emergency assistance",
              "crisis intervention",
              "personalised medical advice",
              "personalised psychological advice",
            ]}
          />
          <p>
            AI is not a therapist or a healthcare professional. Please use your
            own judgement and seek an appropriately qualified professional where
            necessary.
          </p>
          <p>
            How AI features handle your information is explained in our{" "}
            <Link to="/ai-disclaimer" className="text-gold underline">
              AI Disclaimer
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-gold underline">
              Privacy Policy
            </Link>
            .
          </p>
        </Section>

        {/* 8 */}
        <Section icon={NotebookPen} title="8. Journals and reflections">
          <p>
            Journals, dream entries and reflective exercises are designed to
            support your own personal reflection, privately.
          </p>
          <p>They are not:</p>
          <Bullets
            items={[
              "clinical records",
              "therapy sessions",
              "diagnostic assessments",
              "professional monitoring services",
            ]}
          />
          <p>
            <strong>
              Recovery Trauma™ does not continuously monitor journal entries for
              indications of risk or crisis.
            </strong>{" "}
            Please do not use a journal or reflection tool as a way of
            contacting emergency or crisis support. If you need urgent help, use
            the services listed at the top of this page.
          </p>
        </Section>

        {/* 9 */}
        <Section icon={Users} title="9. Community content">
          <p>
            Content posted by members reflects the views and experiences of
            individual users. It should not be treated as medical advice,
            therapy, professional mental-health advice or professional
            recommendations.
          </p>
          <p>
            Recovery Trauma™ does not endorse every statement made by community
            members. Please use your own judgement when reading peer
            experiences, and take what is useful for you.
          </p>
          <p>
            Reporting, blocking and moderation are described in our{" "}
            <Link to="/community-guidelines" className="text-gold underline">
              Community Guidelines
            </Link>
            .
          </p>
        </Section>

        {/* 10 */}
        <Section icon={UserCheck} title="10. Therapist directory">
          <p>
            Recovery Trauma™ offers therapist discovery. Therapists listed
            operate <strong>independently</strong>. Recovery Trauma™ does not
            provide the therapy.
          </p>
          <p>
            We may administratively verify information or documentation supplied
            for directory participation. That administrative step does not
            constitute:
          </p>
          <Bullets
            items={[
              "clinical supervision",
              "ongoing monitoring of clinical practice",
              "a guarantee of suitability",
              "a guarantee of outcome",
              "an endorsement of every professional decision a therapist makes",
            ]}
          />
          <p>Therapists remain independently responsible for their:</p>
          <Bullets
            items={[
              "professional practice",
              "qualifications",
              "registration and licensing",
              "insurance",
              "professional obligations",
              "clinical decisions",
              "client care",
              "safeguarding obligations",
              "compliance with applicable professional standards",
            ]}
          />
          <p>
            You remain responsible for deciding whether a particular therapist
            is appropriate for your circumstances. See the{" "}
            <Link to="/therapist-directory" className="text-gold underline">
              therapist directory
            </Link>{" "}
            for more information.
          </p>
        </Section>

        {/* 11 */}
        <Section icon={CalendarDays} title="11. Events and seminars">
          <p>
            Recovery Trauma™ seminars, workshops and educational events are for
            educational and wellbeing purposes unless we explicitly state
            otherwise. Attending does not ordinarily create a therapeutic
            relationship, and these sessions are not therapy or clinical
            treatment.
          </p>
          <p>
            Where participants share personal experiences, please respect their
            privacy — do not record, screenshot or share what others disclose.
          </p>
        </Section>

        {/* 12 */}
        <Section icon={AlertTriangle} tone="warning" title="12. Self-harm and levels of risk">
          <p>
            Different situations need different kinds of help, and we will not
            try to judge severity through wording on a page.
          </p>
          <Bullets
            items={[
              <>
                <strong>Immediate danger or imminent risk</strong> — contact your
                local emergency services (999 in the UK, 911 in the US, or your
                local number).
              </>,
              <>
                <strong>Emotional distress, or needing someone to talk to</strong>{" "}
                — contact an appropriate crisis or support service, such as
                Samaritans on 116 123 in the UK and Ireland, or 988 in the US.
              </>,
            ]}
          />
          <p>
            Our{" "}
            <Link to="/support" className="text-gold underline">
              support resources
            </Link>{" "}
            page lists options by country.
          </p>
        </Section>

        {/* 13 */}
        <Section icon={ShieldAlert} tone="warning" title="13. No continuous monitoring">
          <p>
            <strong>
              Recovery Trauma™ is not continuously monitored for crisis or
              emergency situations.
            </strong>
          </p>
          <p>
            Reports, journals, community posts, contact forms, emails,
            therapist-directory interactions, AI features and other platform
            features must not be relied upon for urgent or emergency assistance.
            We cannot promise immediate intervention.
          </p>
        </Section>

        {/* 14 */}
        <Section icon={Sparkles} title="14. No guaranteed outcomes">
          <p>Recovery Trauma™ does not guarantee:</p>
          <Bullets
            items={[
              "recovery",
              "symptom improvement",
              "reduced trauma symptoms",
              "a particular wellbeing result",
              "success from any exercise",
              "suitability of every activity",
              "a specific therapy outcome",
              "the availability or suitability of a particular therapist",
            ]}
          />
          <p>
            People respond differently to wellbeing tools and educational
            material. There is no single right pace or path.
          </p>
        </Section>

        {/* 15 */}
        <Section icon={Heart} title="15. Professional support">
          <p>
            Recovery Trauma™ can complement, but does not replace, appropriate
            professional support.
          </p>
          <p>
            You may wish to consider seeking qualified professional support
            where:
          </p>
          <Bullets
            items={[
              "symptoms are severe",
              "symptoms persist or worsen",
              "you are concerned about your physical or mental health",
              "a practice feels inappropriate for you",
              "you need diagnosis, treatment or personalised clinical advice",
            ]}
          />
          <p>
            This is information, not an instruction — you know your own
            situation best.
          </p>
        </Section>

        {/* 16 */}
        <Section icon={ShieldAlert} title="16. Age requirement">
          <p>
            <strong>
              Recovery Trauma™ is intended for adults aged 18 and over.
            </strong>{" "}
            This applies to accounts, membership, community, events and the app,
            and is consistent with our{" "}
            <Link to="/terms" className="text-gold underline">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-gold underline">
              Privacy Policy
            </Link>
            .
          </p>
        </Section>

        {/* 17 */}
        <Section icon={Globe} title="17. Third-party information and services">
          <p>
            Recovery Trauma™ may provide links, integrations, information or
            access relating to third parties, including independent therapists,
            crisis and support organisations, external websites, music services,
            payment services and other resources.
          </p>
          <p>
            We do not control all third-party services, and we are not
            responsible for their independent professional advice, availability
            or content, except to the extent that applicable law makes us
            responsible.
          </p>
        </Section>

        {/* 18 */}
        <Section icon={Scale} title="18. Your use of the platform">
          <p>
            <strong>
              By using Recovery Trauma™, you acknowledge that you understand the
              nature and limitations of the platform. You remain responsible for
              deciding how you use general wellbeing and educational resources,
              and for seeking appropriate professional or emergency support when
              required.
            </strong>
          </p>
          <p>
            <strong>
              Nothing in this Disclaimer excludes or limits rights or liability
              that cannot lawfully be excluded or limited.
            </strong>
          </p>
          <p>
            This page explains the limits and intended use of the platform. The
            contractual relationship between you and RECOVERY TRAUMA LTD is
            governed by our{" "}
            <Link to="/terms" className="text-gold underline">
              Terms of Service
            </Link>
            .
          </p>
        </Section>

        {/* 19 */}
        <Section icon={BookOpen} title="19. Related pages">
          <Bullets
            items={[
              <Link to="/support" className="text-gold underline">
                Crisis &amp; support resources
              </Link>,
              <Link to="/crisis" className="text-gold underline">
                Crisis support
              </Link>,
              <Link to="/terms" className="text-gold underline">
                Terms of Service
              </Link>,
              <Link to="/privacy" className="text-gold underline">
                Privacy Policy
              </Link>,
              <Link to="/community-guidelines" className="text-gold underline">
                Community Guidelines
              </Link>,
              <Link to="/ai-disclaimer" className="text-gold underline">
                AI Disclaimer
              </Link>,
              <Link to="/therapist-directory" className="text-gold underline">
                Therapist directory
              </Link>,
            ]}
          />
        </Section>

        {/* 20 */}
        <Section icon={Mail} tone="gold" title="20. Contact">
          <p>
            Questions about this Disclaimer or anything on the platform:{" "}
            <a
              href="mailto:info@recoverytrauma.com"
              className="font-semibold text-gold underline-offset-2 hover:underline"
            >
              info@recoverytrauma.com
            </a>{" "}
            ·{" "}
            <a
              href="https://www.recoverytrauma.com"
              className="font-semibold text-gold underline-offset-2 hover:underline"
            >
              www.recoverytrauma.com
            </a>
          </p>
          <div className="mt-3">
            <CompanyInfo />
          </div>
          <p className="pt-2 text-xs text-muted-foreground">
            We review this Disclaimer whenever there is a significant change to
            platform functionality, AI use, the therapist directory, community,
            assessments, app functionality, relevant laws or our risk profile.
          </p>
        </Section>
      </div>

      <div className="mt-8 text-center">
        <p className="text-xs text-muted-foreground">
          Last updated: {LAST_UPDATED}
        </p>
      </div>
    </div>
  );
}
